/**
 * @packageDocumentation
 * @module StockChartModule
 */
import { Component, OnInit, Input } from '@angular/core';
import { DatePickerConfig } from '../stock-chart/stock-chart-model';
import { Subject, Subscription } from 'rxjs';
import { isEmptyField, isEmptyObject, getFormattedDate, convertToInt } from '../stock-chart-shared/stock-chart-shared-services/stock-chart-util.service';
import { StockChartService } from '../stock-chart.service';
import { FormGroup, FormControl } from '@angular/forms';
import { NgbCalendar } from '@ng-bootstrap/ng-bootstrap';
import { StockChartDateFormatService } from '../stock-chart-shared/stock-chart-shared-services/stock-chart-date-format.service';

@Component({
  selector: 'app-stock-chart-date-handler',
  templateUrl: './stock-chart-date-handler.component.html',
  styleUrls: ['./stock-chart-date-handler.component.scss']
})
export class StockChartDateHandlerComponent implements OnInit {
  /**
   * datePickerConfig will be taken from stock config
   */
  @Input() datePickerConfig: DatePickerConfig;
  /**
   * stock chart ID
   */
  @Input() stockChartId: string;
  datePickerFormGroup = new FormGroup({});
  minDate: string;
  maxDate: string;
  minDatePickerDate: any;
  maxDatePickerDate: any;
  maxDateForStartDate: any;
  showFormGroup = false;
  timeSpanConfiguration: any;

  // kickStart Dependencies
  kickStartRequiredArray: string[] = [];
  kickStartRequiredObject: any = {};
  kickStartSubject = new Subject<any>();

  // filter Variables Start
  filterSubscription: Subscription;
  stockCharFilterData: any = {};
  filterAllowedChangedFields = ['timeInterval'];

  constructor(
    private stockChartService: StockChartService,
    private dateFormatService: StockChartDateFormatService,
    private ngbCalendar: NgbCalendar
  ) {
  }
  ngOnInit() {
    if (isEmptyObject(this.datePickerConfig)) {
      return;
    }
    this.datePickerConfig.kickStartRequired = this.datePickerConfig.kickStartRequired ? this.datePickerConfig.kickStartRequired : [];
    if (this.stockChartService.initSubjects && this.stockChartService.initSubjects[this.stockChartId]) {
      this.stockChartService.initSubjects[this.stockChartId].date.next(true);
    }
    this.handleSubscriptions();
    this.setDefaultMinMaxDates();
    // this.initComponent();
  }
  /**
   * will set default min and max date for the datePickers
   */
  setDefaultMinMaxDates() {
    this.minDate = JSON.stringify(JSON.parse(this.datePickerConfig.minDate));
    if (this.datePickerConfig.maxDate === 'today') {
      this.maxDate = getFormattedDate();
    } else {
      this.maxDate = JSON.stringify(JSON.parse(this.datePickerConfig.maxDate));
    }
    this.minDatePickerDate = this.convertToDate(this.minDate);
    this.maxDatePickerDate = this.convertToDate(this.maxDate);
    this.maxDateForStartDate = this.ngbCalendar.getToday();
  }
  /**
   * Initiliazation of the component
   * will set the start date and end dates from config
   */
  initComponent() {
    this.timeSpanConfiguration = this.datePickerConfig.timeIntervalConfig[this.stockCharFilterData.timeInterval];
    let subtractedStartDate: any;
    if (this.timeSpanConfiguration === 'tillMinDate') {
      this.setDefaultMinMaxDates();
      subtractedStartDate = JSON.parse(JSON.stringify(this.minDate));
    } else {
      this.setDefaultMinMaxDates();
      subtractedStartDate = this.dateFormatService.subTractYears(this.maxDate, this.timeSpanConfiguration);
    }

    if (!this.datePickerFormGroup.get('startDate')) {
      this.datePickerFormGroup.addControl('startDate', new FormControl(subtractedStartDate));
    } else {
      this.datePickerFormGroup.get('startDate').setValue(subtractedStartDate);
    }

    if (!this.datePickerFormGroup.get('endDate')) {
      this.datePickerFormGroup.addControl('endDate', new FormControl(this.maxDate));
    } else {
      this.datePickerFormGroup.get('endDate').setValue(this.maxDate);
    }
    this.maxDatePickerDate = this.convertToDate(this.maxDate);
    this.showFormGroup = true;
    this.stockChartService.updateDatePickerForm(this.stockChartId, this.datePickerFormGroup.value)
  }
  /**
   * startDate change event will set the end dates accordingly
   * @param event start date from the event change
   */
  startDateChange(event) {
    if (this.timeSpanConfiguration == 'tillMinDate') {
      return;
    }
    let maxDateAfterAdd = this.dateFormatService.addYears(this.datePickerFormGroup.value.startDate, this.timeSpanConfiguration);
    let today = getFormattedDate();

    let currentEndDate = this.datePickerFormGroup.get('endDate').value;
    let currentStartDate = this.datePickerFormGroup.get('startDate').value;
    this.maxDate = maxDateAfterAdd > today ? today : maxDateAfterAdd;
    if (convertToInt(currentEndDate)  > convertToInt(this.maxDate) || convertToInt(currentEndDate) < convertToInt(currentStartDate)) {
      this.datePickerFormGroup.get('endDate').setValue(this.maxDate);
    }
    this.maxDatePickerDate = this.convertToDate(this.maxDate);
  }
  /**
   * date picker submit
   */
  submit() {
    this.stockChartService.updateDatePickerForm(this.stockChartId, this.datePickerFormGroup.value);
  }
  /**
   * convert the moment date to NgbDate
   * @param date start date / end date
   */
  convertToDate(date) {
    let momentDate = this.dateFormatService.formatToMoment(date);
    return {
      day : momentDate.date(),
      month: momentDate.month() + 1,
      year: momentDate.year()
    };
  }
  /**
   * will handle the major subscription like filter (kick start dependencies)
   */
  handleSubscriptions() {
    this.kickStartRequiredArray = this.datePickerConfig.kickStartRequired;
    if (this.kickStartRequiredArray.length == 0) {
      this.initComponent();
    } else {
      this.kickStartRequiredArray.forEach((element) => {
        this.kickStartRequiredObject[element] = false;
      });
      this.kickStartSubject.subscribe(data => {
        let kickStart = true;
        Object.values(data).forEach(value => {
          if (isEmptyField(value) || value === false) {
            kickStart = false;
          }
        });
        if (kickStart) {
          this.initComponent();
        }
      });
      this.subscribeToKickStartComponents();
    }
  }
  /**
   * child function to handle subscriptions
   */
  subscribeToKickStartComponents() {
    this.kickStartRequiredArray.forEach(element => {
      if (element == 'filter') {
        this.stockChartService.createFilterSubject(this.stockChartId);
        this.stockChartService.initSubjects[this.stockChartId][element].subscribe ( initData => {
          if (initData) {
            this.filterSubscription = this.stockChartService.filterFormSubjects[this.stockChartId]
              .subscribe( data => {
                this.stockCharFilterData = data.data;
                const hasToChange = this.isNeedToChange(data, 'filter');
                if (hasToChange) {
                  this.initComponent();
                }
                // kick start for the first time
                if (!isEmptyObject(this.kickStartRequiredObject) && !isEmptyField(this.kickStartRequiredObject.filter) && !this.kickStartRequiredObject.filter) {
                  this.kickStartRequiredObject.filter = true;
                  this.kickStartSubject.next(this.kickStartRequiredObject);
                }
              });
          }
        });
      }
    });
  }
  /**
   * to detect whether the change is there or not
   */
  isNeedToChange(data, component) {
    const fieldsChanged = !isEmptyObject(data.changedFields) ? data.changedFields : [];
    const allowedFields = component === 'filter' ? this.filterAllowedChangedFields : [];
    let hasToChange = false;
    if (fieldsChanged.length > 0) {
      fieldsChanged.forEach(field => {
        if (!hasToChange && allowedFields.indexOf(field) !== -1) {
          hasToChange = true;
        }
      });
    }
    return hasToChange;
  }

  // tslint:disable-next-line: use-life-cycle-interface
  /**
   * Angular Destroy function
   * will unsubscribe and clear the data
   */
  ngOnDestroy() {
    this.stockChartService.resetFilterData();
    if(this.filterSubscription){
      this.filterSubscription.unsubscribe();
    }
  }


}
